/**
 * NotificationCenter Component - APB-1296
 *
 * Epic: APB-1306 - [Notification] EPIC-2 - Approval Workflow Notifications (P1)
 *
 * Main notification center dropdown that displays in the TopBar.
 * Features:
 * - Bell icon with unread count badge
 * - Dropdown with notification list
 * - Filter by notification type
 * - Mark all as read
 * - Click outside to close
 * - Polling for new notifications
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { notificationsApi, Notification, NotificationPriority } from '../../shared/services/notificationsApi';
import { NotificationItem } from './NotificationItem';

// =============================================================================
// CONSTANTS
// =============================================================================

const POLL_INTERVAL_MS = 30000; // Poll every 30 seconds
const MAX_VISIBLE_NOTIFICATIONS = 10;

// =============================================================================
// TYPES
// =============================================================================

interface NotificationCenterProps {
  className?: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const NotificationCenter: React.FC<NotificationCenterProps> = ({ className = '' }) => {
  // State
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadByPriority, setUnreadByPriority] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string | null>(null);

  // Refs
  const dropdownRef = useRef<HTMLDivElement>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // =============================================================================
  // DATA FETCHING
  // =============================================================================

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await notificationsApi.getUnreadCount();
      setUnreadCount(response.unread_count);
      setUnreadByPriority(response.by_priority);
    } catch (err: any) {
      console.error('Failed to fetch unread count:', err);
      setUnreadCount(0);
      setUnreadByPriority({});
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params: Record<string, unknown> = {
        page: 1,
        page_size: MAX_VISIBLE_NOTIFICATIONS,
      };

      if (filter) {
        params.notification_type = filter;
      }

      const response = await notificationsApi.listNotifications(params);
      setNotifications(response.notifications);
      setUnreadCount(response.unread_count);
    } catch (err: any) {
      let errorMessage = 'Failed to load notifications';
      
      if (err?.message) {
        if (err.message.includes('404') || err.message.includes('Not Found')) {
          errorMessage = 'Notification service not available';
        } else if (err.message.includes('401') || err.message.includes('Unauthorized')) {
          errorMessage = 'Authentication required';
        } else if (err.message.includes('403') || err.message.includes('Forbidden')) {
          errorMessage = 'Access denied';
        } else if (err.message.includes('Network')) {
          errorMessage = 'Network error - please check your connection';
        } else if (err.message.includes('timeout')) {
          errorMessage = 'Request timed out';
        }
      }
      
      setError(errorMessage);
      console.error('Failed to fetch notifications:', err);
      
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  // =============================================================================
  // HANDLERS
  // =============================================================================

  const handleToggle = useCallback(() => {
    setIsOpen(prev => {
      if (!prev) {
        // Opening - fetch notifications
        fetchNotifications();
      }
      return !prev;
    });
  }, [fetchNotifications]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleMarkAsRead = useCallback(async (notificationId: string) => {
    try {
      await notificationsApi.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId
            ? { ...n, is_read: true, read_at: new Date().toISOString() }
            : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  }, []);

  const handleDismiss = useCallback(async (notificationId: string) => {
    try {
      await notificationsApi.deleteNotification(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      // Update unread count if the dismissed notification was unread
      const notification = notifications.find(n => n.id === notificationId);
      if (notification && !notification.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Failed to dismiss notification:', err);
    }
  }, [notifications]);

  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await notificationsApi.markAllAsRead(filter || undefined);
      setNotifications(prev =>
        prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  }, [filter]);

  const handleFilterChange = useCallback((newFilter: string | null) => {
    setFilter(newFilter);
  }, []);

  // =============================================================================
  // EFFECTS
  // =============================================================================

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Initial fetch and polling
  useEffect(() => {
    // Initial fetch
    fetchUnreadCount();

    // Set up polling
    pollIntervalRef.current = setInterval(() => {
      if (!isOpen) {
        fetchUnreadCount();
      }
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [fetchUnreadCount, isOpen]);

  // Refetch when filter changes
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [filter, isOpen, fetchNotifications]);

  // =============================================================================
  // RENDER HELPERS
  // =============================================================================

  const getBadgeColor = (): string => {
    if (unreadByPriority[NotificationPriority.CRITICAL] > 0) {
      return 'bg-red-500';
    }
    if (unreadByPriority[NotificationPriority.HIGH] > 0) {
      return 'bg-orange-500';
    }
    return 'bg-blue-500';
  };

  // =============================================================================
  // RENDER
  // =============================================================================

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {/* Bell Icon Button */}
      <button
        onClick={handleToggle}
        className="relative p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800 transition-colors"
        aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
      >
        {/* Bell Icon */}
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>

        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span
            className={`
              absolute top-0 right-0 inline-flex items-center justify-center
              min-w-[18px] h-[18px] px-1 text-xs font-bold text-white
              ${getBadgeColor()} rounded-full transform translate-x-1 -translate-y-1
            `}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 max-h-[calc(100vh-100px)] bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-50 flex flex-col">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Notifications</h3>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                >
                  Mark all as read
                </button>
              )}
            </div>

            {/* Filter Tabs */}
            <div className="flex items-center space-x-2 mt-3">
              <button
                onClick={() => handleFilterChange(null)}
                className={`px-3 py-1 text-xs rounded-full transition-colors ${
                  filter === null
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                All
              </button>
              <button
                onClick={() => handleFilterChange('approval_required')}
                className={`px-3 py-1 text-xs rounded-full transition-colors ${
                  filter === 'approval_required'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                Approvals
              </button>
              <button
                onClick={() => handleFilterChange('alert_critical')}
                className={`px-3 py-1 text-xs rounded-full transition-colors ${
                  filter === 'alert_critical'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                Alerts
              </button>
              <button
                onClick={() => handleFilterChange('system')}
                className={`px-3 py-1 text-xs rounded-full transition-colors ${
                  filter === 'system'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                System
              </button>
            </div>
          </div>

          {/* Notification List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-600 border-t-blue-500" />
              </div>
            ) : error ? (
              <div className="px-4 py-8 text-center">
                <p className="text-red-400 text-sm">{error}</p>
                <button
                  onClick={fetchNotifications}
                  className="mt-2 text-xs text-blue-400 hover:text-blue-300"
                >
                  Retry
                </button>
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <svg
                  className="w-12 h-12 mx-auto text-gray-600 mb-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>
                <p className="text-gray-400 text-sm">No notifications</p>
                <p className="text-gray-500 text-xs mt-1">
                  {filter ? 'Try a different filter' : "You're all caught up!"}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-800">
                {notifications.map(notification => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={handleMarkAsRead}
                    onDismiss={handleDismiss}
                    onClose={handleClose}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-gray-700 text-center">
              <button
                onClick={() => {
                  handleClose();
                  // Navigate to full notifications page if it exists
                  window.location.href = '/notifications';
                }}
                className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
